import type { DirectusLocation, GeoPoint, LngLat, MapLocation } from './types';

export interface NormalizeResult {
  locations: MapLocation[];
  /** Human-readable data problems, for logging and for fixing in Directus. */
  problems: string[];
}

function toNumber(value: unknown): number {
  return typeof value === 'number' ? value : Number.parseFloat(String(value ?? ''));
}

function validLngLat(lng: number, lat: number): LngLat | null {
  if (!Number.isFinite(lng) || !Number.isFinite(lat)) return null;
  if (lng < -180 || lng > 180 || lat < -90 || lat > 90) return null;
  return [lng, lat];
}

/**
 * Resolve a row's position. Editors either pick a point on the map (`coords`) or tick
 * `input_lat_long_directly` and type lat/long. Honor the flag, but fall back to the other
 * source rather than dropping the location if the preferred one is empty.
 */
export function resolveLngLat(row: DirectusLocation): LngLat | null {
  const typed = validLngLat(toNumber(row.longitude), toNumber(row.latitude));
  const picked = row.coords?.coordinates
    ? validLngLat(toNumber(row.coords.coordinates[0]), toNumber(row.coords.coordinates[1]))
    : null;
  return row.input_lat_long_directly ? (typed ?? picked) : (picked ?? typed);
}

export function pointLngLat(point: GeoPoint | null | undefined): LngLat | null {
  const c = point?.coordinates;
  return c ? validLngLat(toNumber(c[0]), toNumber(c[1])) : null;
}

/** Only http(s) links: the value comes from the CMS and ends up in an href. */
export function safeUrl(value: string | null | undefined): string | null {
  const v = value?.trim();
  if (!v) return null;
  try {
    const url = new URL(v);
    return url.protocol === 'https:' || url.protocol === 'http:' ? url.href : null;
  } catch {
    return null;
  }
}

function cleanAliases(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return [...new Set(value.filter((a): a is string => typeof a === 'string').map((a) => a.trim()).filter(Boolean))];
}

export function categoryOf(row: DirectusLocation): string | null {
  const key = row.location_type === 'parking' ? row.parking_type : row.building_type;
  return key || null;
}

export function normalizeLocations(rows: DirectusLocation[]): NormalizeResult {
  const problems: string[] = [];
  const bySlug = new Map<string, MapLocation>();

  // Lowest id wins when slugs collide, so the result doesn't depend on API ordering.
  for (const row of [...rows].sort((a, b) => a.id - b.id)) {
    const label = `"${row.title}" (id ${row.id})`;
    // Slugs are URL paths; treat "Eastman-institute" and "eastman-institute" as the same one.
    const slug = row.slug?.trim().toLowerCase();
    if (!slug) {
      problems.push(`${label} has no slug`);
      continue;
    }
    const category = categoryOf(row);
    if (!category) {
      problems.push(`${label} has no ${row.location_type === 'parking' ? 'parking' : 'building'} type`);
      continue;
    }
    const lngLat = resolveLngLat(row);
    if (!lngLat) {
      problems.push(`${label} has no usable coordinates`);
      continue;
    }
    const existing = bySlug.get(slug);
    if (existing) {
      problems.push(`${label} duplicates slug "${slug}" of id ${existing.id}; keeping id ${existing.id}`);
      continue;
    }
    bySlug.set(slug, {
      id: row.id,
      slug,
      title: row.title.trim(),
      description: row.description?.trim() ?? '',
      campus: row.campus,
      type: row.location_type === 'parking' ? 'parking' : 'building',
      category,
      lngLat,
      imageId: row.image || null,
      aliases: cleanAliases(row.aliases),
      address: row.address?.trim() ?? '',
      url: safeUrl(row.url),
      entrance: pointLngLat(row.entrance),
      accessibleEntrance: pointLngLat(row.accessible_entrance),
      accessibilityNotes: row.accessibility_notes?.trim() ?? '',
    });
  }

  const locations = [...bySlug.values()].sort((a, b) =>
    a.title.localeCompare(b.title, 'en', { sensitivity: 'base', numeric: true }),
  );
  return { locations, problems };
}
